import { InfoWindow, Marker } from '@react-google-maps/api'
import { Title } from 'rbx'
import moment from 'moment'
import pinMonitorOnline from '../../../assets/pin-monitor-online.png'
import pinMonitorOffline from '../../../assets/pin-monitor-offline.png'

// ─── Por que esta camada existe ──────────────────────────────────────────────
// Ela é a ÚNICA dona do conceito de identidade de um monitor. A mesma expressão
// serve de `key` do <Marker> E de valor comparado com `activeMarker`. Inline no
// MonitorsNow essa expressão apareceria duplicada nos dois lugares — e é
// exatamente essa duplicação que produzia o bug de identidade.
//
// O bug que isto conserta (tela branca ao clicar num pin):
// `key={Math.random()}` fazia TODAS as chaves mudarem a cada render. Ao clicar,
// `setActiveMarker` re-renderizava, o React desmontava e remontava a lista
// inteira, e o marker ativo nascia já com o <InfoWindow> dentro no PRIMEIRO
// render. Só que no @react-google-maps/api:
//
//   Marker.render()      → cloneElement(child, { anchor: this.marker })
//   InfoWindow.open()    → if (anchor) ... else if (getPosition()) ... else invariant(false)
//
// `this.marker` só passa a existir no componentDidMount do Marker, e o React roda
// o componentDidMount dos filhos ANTES do pai. Num Marker recém-montado o anchor
// chega `undefined`, o InfoWindow não tem `position`, o invariant lança, a
// exceção não é capturada e a árvore inteira desmonta → tela branca.
//
// Com chave estável o Marker apenas ATUALIZA em vez de remontar: `this.marker` já
// existe quando o InfoWindow monta, e `infoWindow.open(map, marker)` funciona.
// É por isso que o crash só acontecia no clique.
// ─────────────────────────────────────────────────────────────────────────────

// Identidade e SOMENTE identidade — deliberadamente sem lat/lng e sem índice.
// Diferença crítica em relação ao MonitorsPointsLayer (FindMonitors), onde os
// pontos são histórico estático: AQUI os monitores se movem e a lista é filtrada
// pela busca. Incluir posição ou índice na chave remontaria os markers a cada
// refresh de 60s e a cada tecla digitada — reintroduzindo exatamente o crash
// acima. A chave precisa sobreviver ao movimento do monitor.
export const monitorKey = (monitor) => monitor?.id ?? monitor?.userId ?? monitor?.name

/**
 * Camada de pins dos monitores em tempo real.
 * Precisa ser renderizada como FILHO do <ZadMap>: o loader do script do Google
 * Maps (useJsApiLoader) vive dentro do ZadMap.
 *
 * @param {Array<{id?:string, userId?:string, name:string, lat:number, lng:number, online?:boolean, date?:string}>} monitors
 * @param {string|number|null} activeMarker  chave (monitorKey) do monitor com InfoWindow aberto
 * @param {(key:string|number)=>void} onMarkerClick
 */
export const MonitorsMarkersLayer = ({
  monitors = [],
  activeMarker = null,
  onMarkerClick
}) => {
  return monitors.map((monitor) => {
    const key = monitorKey(monitor)

    // `key != null` não é defensividade decorativa: sem ele, um monitor sem
    // identidade cairia em `undefined === undefined` e abriria TODOS os
    // InfoWindows de uma vez.
    const isActive = key != null && key === activeMarker

    return (
      <Marker
        key={key}
        title={monitor.name}
        icon={monitor.online ? pinMonitorOnline : pinMonitorOffline}
        position={{ lat: Number(monitor.lat), lng: Number(monitor.lng) }}
        onClick={() => onMarkerClick && onMarkerClick(key)}
      >
        {
          // `null`, nunca `<></>`: um Fragment passa em React.isValidElement, então
          // o Marker o clona com a prop `anchor` e o React reclama
          // ("Invalid prop `anchor` supplied to `React.Fragment`"). `null` é
          // ignorado por React.Children.map.
          isActive ? (
            <InfoWindow>
              <div style={{ background: 'white', border: '1px solid #ccc', padding: 15 }}>
                <Title size={6}>{monitor.name}</Title>
                <Title subtitle size={7}>{moment(monitor.date).format('DD/MM/YYYY HH:mm:ss')}</Title>
              </div>
            </InfoWindow>
          ) : null
        }
      </Marker>
    )
  })
}

export default MonitorsMarkersLayer
